import {
  Archive,
  ArchiveRestore,
  Download,
  FileText,
  Loader2,
  MoreHorizontal,
  Play,
  Sprout,
  Trash2,
  X,
} from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
  useArchiveProjectMutation,
  useCancelProjectMutation,
  useContinueProjectMutation,
  useDeleteProjectMutation,
} from "@/entities/projects/queries";
import { ExpandDialog } from "@/widgets/ProjectPage/ExpandDialog";
import type { ProjectOut } from "@/entities/projects/types";
import { ApiError } from "@/shared/api/http";
import { cn } from "@/shared/lib/cn";
import { formatNumber, formatTimeAgo } from "@/shared/lib/format";
import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  queued:      { label: "В очереди", color: "bg-amber/15 text-amber border-amber/35" },
  collecting:  { label: "Сбор",       color: "bg-cyan/15 text-cyan border-cyan/35" },
  clustering:  { label: "Кластеризация", color: "bg-purple/15 text-purple border-purple/35" },
  done:        { label: "Готово",     color: "bg-green/15 text-green border-green/35" },
  partial:     { label: "Частично",   color: "bg-amber/15 text-amber border-amber/35" },
  failed:      { label: "Ошибка",     color: "bg-pink/15 text-pink border-pink/35" },
  cancelled:   { label: "Отменено",   color: "bg-white/[0.05] text-muted-foreground border-white/15" },
};

const ACTIVE_STATUSES = new Set(["queued", "collecting", "clustering"]);

export interface ProjectHeaderProps {
  project: ProjectOut;
  clustersCount: number;
  /** Открыть Sheet с журналом сбора (PipelineSteps + Log). */
  onOpenLog?: () => void;
}

export function ProjectHeader({
  project,
  clustersCount,
  onOpenLog,
}: ProjectHeaderProps): JSX.Element {
  const navigate = useNavigate();
  const cancelM = useCancelProjectMutation();
  const archiveM = useArchiveProjectMutation();
  const deleteM = useDeleteProjectMutation();
  const continueM = useContinueProjectMutation();

  const [expandDialogOpen, setExpandDialogOpen] = React.useState(false);

  const status = STATUS_LABEL[project.status] ?? STATUS_LABEL.queued!;
  const isActive = ACTIVE_STATUSES.has(project.status);
  const canContinue =
    project.status === "partial" || project.status === "failed";
  const canExpand =
    (project.status === "done" || project.status === "partial") &&
    project.expansion_depth < 2;

  const onContinue = (): void => {
    continueM.mutate(project.id, {
      onSuccess: () => toast.success("Сбор возобновлён"),
      onError: (e) =>
        toast.error(e instanceof ApiError ? e.message : "Не удалось продолжить"),
    });
  };
  const onCancel = (): void => {
    if (!confirm("Отменить сбор? Уже собранные кластеры останутся.")) return;
    cancelM.mutate(project.id, {
      onSuccess: () => toast.success("Сбор отменён"),
      onError: (e) =>
        toast.error(e instanceof ApiError ? e.message : "Не удалось отменить"),
    });
  };
  const onArchive = (): void => {
    archiveM.mutate(
      { id: project.id, on: !project.is_archived },
      {
        onSuccess: () =>
          toast.success(project.is_archived ? "Восстановлен" : "В архив"),
        onError: (e) =>
          toast.error(e instanceof ApiError ? e.message : "Не удалось"),
      },
    );
  };
  // Расширение теперь через ExpandDialog — confirm() убрал, потому что
  // в нём не было видно ни стоимости, ни прогноза.
  const onDelete = (): void => {
    if (
      !confirm(
        `Удалить проект «${project.name}»? Это удалит кластеры, фразы и аналитики.`,
      )
    )
      return;
    deleteM.mutate(project.id, {
      onSuccess: () => {
        toast.success("Проект удалён");
        navigate("/", { replace: true });
      },
      onError: (e) =>
        toast.error(e instanceof ApiError ? e.message : "Не удалось удалить"),
    });
  };

  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 flex-1">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-bold text-white sm:text-3xl">
            {project.name}
          </h1>
          <span
            className={cn(
              "rounded-md border px-2 py-0.5 text-xs font-bold uppercase tracking-wider",
              status.color,
            )}
          >
            {status.label}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span>создан {formatTimeAgo(project.created_at)}</span>
          {project.requests_used > 0 && (
            <>
              <span>·</span>
              <span>{formatNumber(project.requests_used)} запросов Wordstat</span>
            </>
          )}
          {clustersCount > 0 && (
            <>
              <span>·</span>
              <span>{formatNumber(clustersCount)} кластеров</span>
            </>
          )}
          {Number(project.cost_rub) > 0 && (
            <>
              <span>·</span>
              <span className="text-cyan">
                {Number(project.cost_rub).toFixed(2)} ₽
              </span>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {canContinue && (
          <Button
            type="button"
            onClick={onContinue}
            disabled={continueM.isPending}
            className="bg-gradient-primary text-white shadow-glow-purple"
          >
            {continueM.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            Продолжить
          </Button>
        )}
        {canExpand && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setExpandDialogOpen(true)}
            className="border-purple/45 bg-purple/[0.08] text-white hover:bg-purple/[0.15]"
            title="Расширить сбор: посмотреть прогноз и запустить"
          >
            <Sprout className="h-4 w-4" />
            Расширить
          </Button>
        )}
        {isActive && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={cancelM.isPending}
            className="border-white/15 bg-white/[0.04]"
          >
            <X className="h-4 w-4" /> Отменить
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="border-white/15 bg-white/[0.04]"
              aria-label="Меню действий"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onOpenLog && (
              <DropdownMenuItem onClick={onOpenLog}>
                <FileText className="h-4 w-4" /> Журнал сбора
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onArchive}>
              {project.is_archived ? (
                <>
                  <ArchiveRestore className="h-4 w-4" /> Восстановить из архива
                </>
              ) : (
                <>
                  <Archive className="h-4 w-4" /> В архив
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onDelete}
              className="text-pink focus:text-pink"
            >
              <Trash2 className="h-4 w-4" /> Удалить проект
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <DownloadButtonInline project={project} />

      <ExpandDialog
        project={project}
        open={expandDialogOpen}
        onOpenChange={setExpandDialogOpen}
      />
    </div>
  );
}

/** Кнопка «Скачать» с дропдауном на 4 формата.
 *
 * HTML — для просмотра в браузере, xlsx — для просмотра в Excel/Numbers,
 * csv — для импорта в Pandas/Google Sheets, md — для Obsidian/Notion.
 * Имя файла бэк отдаёт через Content-Disposition; здесь только грубый
 * fallback на случай если заголовок не пришёл.
 */
function DownloadButtonInline({
  project,
}: {
  project: ProjectOut;
}): JSX.Element | null {
  const [downloading, setDownloading] = React.useState<
    "html" | "xlsx" | "csv" | "md" | null
  >(null);

  if (!(project.status === "done" || project.status === "partial")) return null;

  const download = async (
    format: "html" | "xlsx" | "csv" | "md",
  ): Promise<void> => {
    setDownloading(format);
    try {
      const mod = await import("@/entities/projects/api");
      const blob = await mod.exportProjectFile(project.id, { format });
      const url = URL.createObjectURL(blob);
      const ext = format === "csv" ? "zip" : format;
      const slug = project.name.replace(/[^a-z0-9]+/gi, "-");
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}-report.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Не удалось скачать отчёт");
      console.error(e);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="border-white/15 bg-white/[0.04]"
          disabled={downloading !== null}
        >
          {downloading !== null ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Скачать
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => void download("html")}>
          HTML <span className="ml-auto text-xs text-muted-foreground">отчёт</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => void download("xlsx")}>
          Excel <span className="ml-auto text-xs text-muted-foreground">.xlsx</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => void download("csv")}>
          CSV <span className="ml-auto text-xs text-muted-foreground">.zip из 3 файлов</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => void download("md")}>
          Markdown <span className="ml-auto text-xs text-muted-foreground">.md</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
